import Link from 'next/link'
import getConfig from 'next/config'
import { useTranslation } from 'next-i18next'
import { makeStyles, Container } from '@material-ui/core'
import T, { specularHost } from '../home-new/tokens'
import Reveal from '../home-new/Reveal'

const { publicRuntimeConfig } = getConfig()

// Section 5 — two-column "connect" block.
// Left: "เชื่อมต่อระบบขนส่งด้วย API" — 4 feature rows + doc buttons.
// Right: "เชื่อมต่อผ่าน Plugin & Module" — WooCommerce / Magento / Widget
// logo cards, each with a guide button.
// Doc/guide hrefs mirror the legacy pages/for-developers.js.

const apiRows = [
  { id: 'price', icon: '/static/img/home-business/api/price.png' },
  { id: 'create', icon: '/static/img/home-business/api/create.png' },
  { id: 'label', icon: '/static/img/home-business/api/label.png' },
  { id: 'track', icon: '/static/img/home-business/api/track.png' },
]

const plugins = [
  {
    id: 'woocommerce',
    logo: '/static/img/home-business/plugin/woocommerce.png',
    alt: 'WooCommerce',
    href: '/static/files/for-developers/SHIPPOP_WooCommerce_manual.pdf',
    external: true,
  },
  {
    id: 'magento',
    logo: '/static/img/home-business/plugin/magento.png',
    alt: 'Magento',
    href: '/static/files/for-developers/SHIPPOP_Magento_manual.pdf',
    external: true,
  },
  {
    id: 'widget',
    logo: '/static/img/home-business/plugin/widget.png',
    alt: 'Widget SHIPPOP',
    href: '/widget',
    wide: true,
  },
]

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '70px 0',
    background: T.color.surface,
    [theme.breakpoints.down('sm')]: {
      padding: '44px 0',
    },
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '48px',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      gap: '48px',
    },
  },
  colHeading: {
    fontFamily: T.font.heading,
    fontSize: 30,
    fontWeight: 700,
    color: T.color.ink,
    textAlign: 'center',
    margin: '0 0 20px',
    lineHeight: 1.25,
    '& span': { color: T.color.brand },
    [theme.breakpoints.down('sm')]: {
      fontSize: 22,
    },
  },
  subPill: {
    display: 'block',
    border: `1.5px solid ${T.color.brand}`,
    borderRadius: T.radius.pill,
    color: T.color.ink,
    fontFamily: T.font.heading,
    fontSize: 16,
    fontWeight: 500,
    textAlign: 'center',
    padding: '12px 20px',
    margin: '0 auto 18px',
    maxWidth: 460,
    [theme.breakpoints.down('sm')]: {
      fontSize: 14,
    },
  },
  colDesc: {
    fontFamily: T.font.body,
    fontSize: 15,
    color: T.color.muted,
    lineHeight: 1.7,
    textAlign: 'center',
    margin: '0 auto 28px',
    maxWidth: 480,
    [theme.breakpoints.down('sm')]: {
      fontSize: 14,
    },
  },
  rows: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: 26,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '18px',
    background: T.color.canvas,
    borderRadius: T.radius.lg,
    padding: '16px 22px',
    boxShadow: T.shadow.sm,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: T.shadow.md,
    },
  },
  rowIcon: {
    flexShrink: 0,
    width: 48,
    height: 48,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& img': {
      maxWidth: 48,
      maxHeight: 48,
      objectFit: 'contain',
    },
  },
  rowTitle: {
    fontFamily: T.font.heading,
    fontSize: 18,
    fontWeight: 600,
    color: T.color.brand,
    margin: '0 0 4px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 16,
    },
  },
  rowDesc: {
    fontFamily: T.font.body,
    fontSize: 14,
    color: T.color.muted,
    lineHeight: 1.5,
    margin: 0,
  },
  btnRow: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '14px',
  },
  btnBlue: {
    ...specularHost,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 46,
    padding: '0 30px',
    borderRadius: T.radius.pill,
    background: T.gradient.blue,
    color: T.color.onBrand,
    fontFamily: T.font.heading,
    fontSize: 15,
    fontWeight: 600,
    textDecoration: 'none',
    boxShadow: '0 6px 18px rgba(0,102,204,0.24)',
    '&:hover': { transform: 'translateY(-2px)', color: T.color.onBrand },
  },
  pluginGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    justifyItems: 'center',
  },
  pluginCard: {
    background: T.color.surface,
    border: `1px solid ${T.color.line}`,
    borderRadius: 20,
    boxShadow: T.shadow.card,
    padding: '30px 24px 24px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 16px 32px rgba(0,102,204,0.12)',
    },
  },
  pluginWide: {
    gridColumn: '1 / -1',
    maxWidth: '60%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
    },
  },
  pluginLogo: {
    height: 90,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 22,
    '& img': {
      maxHeight: 72,
      maxWidth: '90%',
      objectFit: 'contain',
    },
  },
  btnDark: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
    padding: '0 24px',
    borderRadius: T.radius.pill,
    background: T.color.ink,
    color: T.color.onBrand,
    fontFamily: T.font.heading,
    fontSize: 14,
    fontWeight: 500,
    textDecoration: 'none',
    transition: 'transform 0.2s ease, background 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      background: T.color.brandDeep,
      color: T.color.onBrand,
    },
  },
}))

const ConnectBiz = () => {
  const classes = useStyles()
  const { t } = useTranslation('business')

  return (
    <section className={classes.root} id="connect">
      <Container maxWidth={false} style={{ maxWidth: T.container }}>
        <div className={classes.grid}>
          {/* LEFT — API */}
          <div>
            <Reveal direction="left">
              <h2 className={classes.colHeading}>
                {t('connect.api.heading1')}
                <br />
                {t('connect.api.heading2')} <span>API</span>
              </h2>
              <span className={classes.subPill}>
                {t('connect.api.subPill')}
              </span>
              <p className={classes.colDesc}>
                {t('connect.api.desc')}
              </p>
            </Reveal>
            <div className={classes.rows}>
              {apiRows.map((r, i) => (
                <Reveal key={r.id} direction="left" delay={i * 0.08}>
                  <div className={classes.row}>
                    <span className={classes.rowIcon}>
                      <img src={r.icon} alt="" aria-hidden="true" loading="lazy" />
                    </span>
                    <div>
                      <h3 className={classes.rowTitle}>{t(`connect.api.rows.${r.id}.title`)}</h3>
                      <p className={classes.rowDesc}>{t(`connect.api.rows.${r.id}.desc`)}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal direction="up">
              <div className={classes.btnRow}>
                <a
                  href="http://developers.shippop.com/seller"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.btnBlue}
                >
                  {t('connect.api.btnDoc')}
                </a>
                <a
                  href={
                    (publicRuntimeConfig && publicRuntimeConfig.postmanURL) ||
                    'https://documenter.getpostman.com/view/10021496/Tzz8qwkE'
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.btnBlue}
                >
                  {t('connect.api.btnPostman')}
                </a>
              </div>
            </Reveal>
          </div>

          {/* RIGHT — Plugin & Module */}
          <div>
            <Reveal direction="right">
              <h2 className={classes.colHeading}>
                {t('connect.plugin.heading1')}
                <br />
                <span>Plugin &amp; Module</span>
              </h2>
              <span className={classes.subPill}>
                {t('connect.plugin.subPill')}
              </span>
              <p className={classes.colDesc}>
                {t('connect.plugin.desc')}
              </p>
            </Reveal>
            <Reveal direction="right" delay={0.1}>
              <div className={classes.pluginGrid}>
                {plugins.map((p) => (
                  <div
                    key={p.id}
                    className={`${classes.pluginCard} ${p.wide ? classes.pluginWide : ''}`}
                  >
                    <span className={classes.pluginLogo}>
                      <img src={p.logo} alt={p.alt} loading="lazy" />
                    </span>
                    {p.external ? (
                      <a
                        href={p.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.btnDark}
                      >
                        {t(`connect.plugin.items.${p.id}.cta`)}
                      </a>
                    ) : (
                      <Link href={p.href}>
                        <a className={classes.btnDark}>{t(`connect.plugin.items.${p.id}.cta`)}</a>
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </Container>
    </section>
  )
}

export default ConnectBiz
